import React, { useRef, useState, useCallback } from 'react';
import { connect } from 'dva';
import { useMount } from 'ahooks';
import Stickyfill from 'stickyfilljs';
import DocumentTitle from 'react-document-title';
import { jumpToPageByH5 } from '@/utils/fn';
import { registerWindowFn, jsConfigAppBar } from '@/utils/bridge';
import { reportClick, reportPage } from '@/utils/report';
import GroupSelect from './components/GroupSelect';
import GroupIncomeSummary from './components/GroupIncomeSummary';
import AllIncomeSummary from './components/AllIncomeSummary';
import AdInfo from './components/AdInfo';
import IncomeChart from './components/IncomeChart.js';
import ActIncomeInfo from './components/ActIncomeInfo/index';
import IncomeDetailList from './components/IncomeDetailList';
import HistoryCashDistModal from './components/HistoryCashDistModal';
import CumulativeIncomeModal from './components/CumulativeIncomeModal';
import Empty from '../components/Empty';
import IncomeModal from './components/Modal';
import { CHART_OPTION_MAP, DEFAULT_GROUP_ITEM, DEFAULT_INCOME_PAGE_SIZE } from './const';
import styles from './index.less';

const PAGE_NAME = 'group_income';

const IncomeListAll = ({
  income: {
    groupList,
    incomeAdsInfo,
    allIncomeDetail: { summaryData, distribution, cashDistribution, itChartData, incomeDetail },
    isAllIncomeDetail,
  },
  location: { query },
  loading,
  dispatch,
}) => {
  const scrollWrapperRef = useRef(null);
  const [showGroupList, setShowGroupList] = useState(false);
  const [showIncomeTrend, setShowIncomeTrend] = useState(true);
  const [currentChartOption, setCurrentChartOption] = useState(CHART_OPTION_MAP[0]);
  const [currentGroup, setCurrentGroup] = useState(DEFAULT_GROUP_ITEM);
  const [currentTab, setCurrentTab] = useState('incomeList');
  const [showModal, setShowModal] = useState();
  const [modalType, setModalType] = useState();
  const [cashDisModalStatus, setCashDisModalStatus] = useState(false);
  const [cumulativeModalStatus, setCumulativeModalStatus] = useState(false);
  const [listOffsetTop, setListOffsetTop] = useState();
  const handleChooseGroupChange = useCallback(
    current => {
      setCurrentTab('incomeList');
      setShowGroupList(false);
      setCurrentGroup(current);
      handleJsConfigAppBar(current);
      reportClick({
        page_name: PAGE_NAME,
        button_name: 'group',
      });
      // 分组改变重新获取所有数据
      getAllData(current);
    },
    [getAllData, handleJsConfigAppBar]
  );

  const handleJsConfigAppBar = useCallback(current => {
    const data = {
      style: 2,
      trailing: {
        text: `${current?.gname} >`,
        text_color: 'FF333333',
        js_callback: 'jsSelectGroup',
        registed: 1,
      },
      title: {
        text: '累计收益',
        registed: 1,
        text_color: 'FF333333',
        background_color: 'FFFFDAB3',
      },
      leading: {
        js_callback: 'jsSelectGroup',
        registed: 1,
      },
      elevation: 0,
    };
    jsConfigAppBar(data);
  });

  const handleShowHistoryCashModal = useCallback(() => {
    reportClick({
      page_name: PAGE_NAME,
      button_name: 'history_cash',
    });
    setCashDisModalStatus(true);
  }, []);

  const handleToIncomeDay = useCallback(() => {
    reportClick({
      page_name: PAGE_NAME,
      button_name: 'sum_yesterday_btn',
    });
    jumpToPageByH5('/income/list/sday');
  }, []);

  const setModalStatus = (visible, type = 'invite') => {
    setShowModal(visible);
    setModalType(type);
  };

  const getChartData = useCallback(
    days => {
      const params = {
        gid: currentGroup.gid,
        ios: 0,
        ids: 0,
        itp: {
          s: 1,
          d: days,
        },
        idp: {
          s: 0,
          ld: '',
          ps: 0,
        },
      };
      dispatch({
        type: 'income/queryUserIncomeGroupList',
        payload: params,
      });
    },
    [currentGroup.gid, dispatch]
  );

  const handleChartOptionChange = useCallback(
    option => {
      setCurrentChartOption(option);
      reportClick({
        page_name: PAGE_NAME,
        button_name: `${option}-day`,
      });
      // 发请求
      getChartData(option);
    },
    [getChartData]
  );

  const chartOptions = CHART_OPTION_MAP.map(option => (
    <li
      className={currentChartOption === option ? styles['on'] : ''}
      key={option}
      onClick={() => handleChartOptionChange(option)}
    >{`${option}天`}</li>
  ));

  const getAllData = useCallback(
    current => {
      // 获取数据
      const params = {
        gid: current ? current.gid : currentGroup.gid,
        ios: 1,
        ids: 1,
        itp: {
          s: 1,
          d: currentChartOption,
        },
        idp: {
          s: 1,
          ld: '',
          ps: DEFAULT_INCOME_PAGE_SIZE,
        },
      };
      dispatch({
        type: 'income/queryUserIncomeGroupList',
        payload: params,
        callback: incomeData => {
          if (incomeData.it?.length > 0) {
            setShowIncomeTrend(true);
          } else {
            setShowIncomeTrend(false);
          }
        },
        failback: () => {
          // setHasMore(false);
        },
      });
    },
    [currentChartOption, currentGroup.gid, dispatch]
  );

  const getGroupList = useCallback(() => {
    const callback = resData => {
      resData.unshift(DEFAULT_GROUP_ITEM);
      parseUrl(resData);
    };
    dispatch({
      type: 'income/getGroupList',
      payload: {
        origin: '3',
      },
      callback,
    });
  }, [dispatch, parseUrl]);

  const parseUrl = groupData => {
    if (query && query.gid) {
      const groupInUrl = groupData.find(item => item.gid === Number(query.gid));
      if (groupInUrl) {
        setCurrentGroup(groupInUrl);
        handleJsConfigAppBar(groupInUrl);
        getAllData(groupInUrl);
        return;
      }
    }
    getAllData();
  };

  const queryIncomeAdsInfo = () => {
    dispatch({
      type: 'income/queryIncomeAdsInfo',
      payload: {},
    });
  };

  const handleTabChange = type => {
    reportClick({
      page_name: PAGE_NAME,
      button_name: type === 'incomeList' ? 'sum_day_btn' : 'sum_histogram_btn',
    });
    setCurrentTab(type);
    // 调整导航栏位置
    const scrollDistance = document.body.scrollTop + document.documentElement.scrollTop;
    if (scrollWrapperRef.current) scrollWrapperRef.current.scrollTo(0, 0);
    window.scrollTo(0, Math.min(scrollDistance, listOffsetTop));
  };

  const handleLoadMoreThrottle = () => {
    // 如果当前正在加载，则不处理
    if (loading.effects['income/queryUserIncomeGroupList']) return;
    const params = {
      gid: currentGroup.gid,
      ios: 0,
      ids: 0,
      itp: {
        s: 0,
        d: 30,
      },
      idp: {
        s: 1,
        ld: incomeDetail[incomeDetail.length - 1].d,
        ps: DEFAULT_INCOME_PAGE_SIZE,
      },
    };
    dispatch({
      type: 'income/queryUserIncomeGroupList',
      payload: params,
      callback: incomeData => {
        // setHasMore(incomeData.ide.length === DEFAULT_INCOME_PAGE_SIZE);
      },
      failback: () => {
        // setHasMore(false);
      },
    });
  };

  useMount(() => {
    reportPage({
      module: 'income',
      page_name: PAGE_NAME,
    });
    registerWindowFn('jsSelectGroup', () => {
      setShowGroupList(true);
    });
    const stickyDom = document.getElementById('tabBox');
    const targetDistance = stickyDom.getBoundingClientRect().top - 42;
    setListOffsetTop(targetDistance);
    if (!('CSS' in window && CSS.supports && CSS.supports('position', 'sticky'))) {
      Stickyfill.addOne(stickyDom);
    }
    getGroupList();
    queryIncomeAdsInfo();
    handleJsConfigAppBar(DEFAULT_GROUP_ITEM);
  });

  return (
    <DocumentTitle title="累计收益">
      <div className={styles.incomeListAllPage}>
        <GroupSelect
          showGroupList={showGroupList}
          setShowGroupList={setShowGroupList}
          groupList={groupList}
          currentGroup={currentGroup}
          chooseGroupOnChange={handleChooseGroupChange}
        />
        <div className={styles.incomeListAll}>
          <div
            className={`${styles.incomeSummaryWrap} ${
              currentGroup.gid === DEFAULT_GROUP_ITEM.gid
                ? styles.allIncomeSummaryWrap
                : styles.groupIncomeSummaryWrap
            }`}
          >
            <div className={styles.allTitleWrap}>
              {currentGroup.gid === DEFAULT_GROUP_ITEM.gid ? (
                <div className={styles.allTitle}>网心云</div>
              ) : (
                <div className={styles.groupTitle}>分组收益</div>
              )}
              <img
                className={styles.allIntro}
                src={require('@/assets/images/income/intro.png')}
                alt="兑换文字"
              />
            </div>
            {currentGroup.gid === DEFAULT_GROUP_ITEM.gid ? (
              <AllIncomeSummary
                pageName={PAGE_NAME}
                calClick={() => setCumulativeModalStatus(true)}
                summaryData={summaryData}
              />
            ) : (
              <GroupIncomeSummary
                pageName={PAGE_NAME}
                calClick={() => setCumulativeModalStatus(true)}
                summaryData={summaryData}
              />
            )}
          </div>
          <AdInfo
            loading={loading.effects['income/queryIncomeAdsInfo']}
            pageName={PAGE_NAME}
            adsInfo={incomeAdsInfo}
          />

          {currentGroup.gid === DEFAULT_GROUP_ITEM.gid && distribution && (
            <div className={`${styles['ubox']} ${styles['act-reward']}`}>
              <ActIncomeInfo
                pageName={PAGE_NAME}
                distribution={distribution}
                setModalStatus={setModalStatus}
              />
              {cashDistribution?.length > 0 && (
                <div onClick={handleShowHistoryCashModal} className={styles.historyDistribution}>
                  *点此查看历史获得的累计现金分布
                  <span className={styles.goIcon}>去看看</span>
                </div>
              )}
            </div>
          )}
          {currentGroup.gid !== DEFAULT_GROUP_ITEM.gid && (
            <div className={`${styles.incomeChartWrap} ${styles.chartWrap}`}>
              {!showIncomeTrend ? (
                <Empty tip="暂无收益趋势数据" />
              ) : (
                <>
                  <IncomeChart hideTitle={false} chartData={itChartData} valName="v" />
                  <ul className={styles.chartTab}>{chartOptions}</ul>
                </>
              )}
            </div>
          )}
          <div className={`${styles['ubox']} ${styles['ubox-income']}`}>
            <div
              className={`${styles['pdd-tit']} ${styles.sticky} ${
                currentGroup.gid === DEFAULT_GROUP_ITEM.gid ? styles.all : styles.group
              }`}
              id="tabBox"
            >
              <h3
                className={`${currentTab === 'incomeList' ? styles.active : ''} `}
                onClick={() => handleTabChange('incomeList')}
              >
                收益日历
              </h3>
              {currentGroup.gid === DEFAULT_GROUP_ITEM.gid && (
                <h3
                  className={currentTab === 'incomeTrend' ? styles.active : ''}
                  onClick={() => handleTabChange('incomeTrend')}
                >
                  收益趋势
                </h3>
              )}
            </div>
            {currentTab === 'incomeList' && (
              <div className={styles.listWrapper} ref={scrollWrapperRef}>
                {incomeDetail?.length > 0 ? (
                  <IncomeDetailList
                    gid={currentGroup.gid}
                    incomeDetail={incomeDetail}
                    hasMore={!isAllIncomeDetail}
                    loadMore={handleLoadMoreThrottle}
                  />
                ) : (
                  <Empty tip="您还没有设备收益哦，快去赚钱吧！" />
                )}
              </div>
            )}
            {currentTab === 'incomeTrend' && (
              <div className={styles.incomeChartWrap}>
                {itChartData?.length > 0 ? (
                  <>
                    <IncomeChart hideTitle={true} chartData={itChartData} valName="v" />
                    <ul className={styles.chartTab}>{chartOptions}</ul>
                  </>
                ) : (
                  <Empty tip="暂无收益趋势数据" />
                )}
              </div>
            )}
          </div>
        </div>
        <IncomeModal visible={showModal} type={modalType} onClose={() => setModalStatus(false)} />
        <HistoryCashDistModal
          visible={cashDisModalStatus}
          onClose={() => setCashDisModalStatus(false)}
          cashDistribution={cashDistribution}
          title="历史累计现金分布"
        />
        <CumulativeIncomeModal
          visible={cumulativeModalStatus}
          onClose={() => setCumulativeModalStatus(false)}
          summaryData={summaryData}
        />
      </div>
    </DocumentTitle>
  );
};

export default connect(({ income, user, loading }) => ({
  income,
  user,
  loading,
}))(IncomeListAll);
